import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from './store/authSlice';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardLayout from './pages/DashboardLayout';
import CommissionReportsPage from './pages/CommissionReportsPage';
import MonthDetailPage from './pages/MonthDetailPage';
import PayoutsPage from './pages/PayoutsPage';
import ConfigurationPage from './pages/ConfigurationPage';
import AccountPage from './pages/AccountPage';

/**
 * Wraps routes that require an authenticated user. Anonymous visitors get
 * bounced to /login.
 */
function RequireAuth({ children }) {
    const isAuthed = useSelector(selectIsAuthenticated);
    if (!isAuthed) {
        return <Navigate to="/login" replace />;
    }
    return children;
}

/**
 * Inverse: keeps already-logged-in users out of /login and /register.
 * Bounces them to the app home.
 */
function RedirectIfAuthed({ children }) {
    const isAuthed = useSelector(selectIsAuthenticated);
    if (isAuthed) {
        return <Navigate to="/" replace />;
    }
    return children;
}

export default function App() {
    return (
        <BrowserRouter>
            <Routes>
                <Route
                    path="/login"
                    element={
                        <RedirectIfAuthed>
                            <LoginPage />
                        </RedirectIfAuthed>
                    }
                />
                <Route
                    path="/register"
                    element={
                        <RedirectIfAuthed>
                            <RegisterPage />
                        </RedirectIfAuthed>
                    }
                />
                <Route
                    path="/forgot-password"
                    element={
                        <RedirectIfAuthed>
                            <ForgotPasswordPage />
                        </RedirectIfAuthed>
                    }
                />
                {/* Deliberately NOT wrapped in RedirectIfAuthed: someone can
                    open a reset link while a (possibly different) account is
                    signed in on this browser, and bouncing them to the app
                    would silently eat the link. */}
                <Route path="/reset-password" element={<ResetPasswordPage />} />

                {/* Authenticated shell with sidebar + header. Children render
                    into <Outlet /> in DashboardLayout. */}
                <Route
                    element={
                        <RequireAuth>
                            <DashboardLayout />
                        </RequireAuth>
                    }
                >
                    {/* Default landing — bounce to commission reports for now,
                        since it's the only thing in the sidebar. */}
                    <Route path="/" element={<Navigate to="/commission-reports" replace />} />
                    <Route path="/commission-reports" element={<CommissionReportsPage />} />
                    <Route
                        path="/commission-reports/:year/:month"
                        element={<MonthDetailPage />}
                    />
                    <Route path="/payouts" element={<PayoutsPage />} />
                    <Route path="/configuration" element={<ConfigurationPage />} />
                    <Route path="/account" element={<AccountPage />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
}